import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import { ROUTE_NAME } from "../router/types/CommonTypes";
import Icons from "../assets/Icons";
import { globalStyles } from "../styles/GlobalStyles";
import { useTranslation } from "react-i18next";
import useAuthValue from "../modules/authModule/hooks/useAuthValue";
import { useEffect, useMemo } from "react";
import {
  CODING_ADMIN_ROUTES_ACCESS,
  NURSE_ROUTES_ACCESS,
  SURGEON_ROUTES_ACCESS,
} from "../utils/Constants";
import { USER_ROLES } from "../modules/userModule/types/CommonTypes";
import getUserRoleAccess, {
  USER_ROLE_ACCESS_KEY,
} from "../helpers/getUserRoleAccess";

const optionsArray = [
  {
    icon: <Icons.Dashboard />,
    activeIcon: <Icons.Dashboard stroke="white" />,
    label: "Dashboard",
    routeName: ROUTE_NAME.ROOT,
    includeRoute: ROUTE_NAME.DASHBOARD,
  },
  {
    icon: <Icons.CaseManagement />,
    activeIcon: <Icons.CaseManagement stroke="white" />,
    label: "Case_Management",
    routeName: ROUTE_NAME.CASE_MANAGEMENT,
    includeRoute: ROUTE_NAME.CASE_MANAGEMENT,
  },
  {
    icon: <Icons.CaseBoard />,
    activeIcon: <Icons.CaseBoard stroke="white"  />,
    label: "Case_Board",
    routeName: ROUTE_NAME.CASE_BOARD,
    includeRoute: ROUTE_NAME.CASE_BOARD,
  },
  {
    icon: <Icons.Coding/>,
    activeIcon: <Icons.Coding stroke="white" />,
    label: "Coding",
    routeName: ROUTE_NAME.CODING,
    includeRoute: ROUTE_NAME.CODING,
  },
  {
    icon: <Icons.Alerts />,
    activeIcon: <Icons.Alerts stroke="white" />,
    label: "Alerts",
    routeName: ROUTE_NAME.ALERTS,
    includeRoute: ROUTE_NAME.ALERTS,
  },
  {
    label: "User_Account",
    routeName: ROUTE_NAME.USER_ACCOUNT,
  },
  {
    icon: <Icons.Prediction />,
    activeIcon: <Icons.Prediction stroke="white" />,
    label: "Predictions",
    routeName: ROUTE_NAME.PREDICTION,
    includeRoute: ROUTE_NAME.PREDICTION,
  },
  {
    icon: <Icons.Tenant />,
    activeIcon: <Icons.Tenant stroke="white" />,
    label: "Tenant",
    routeName: ROUTE_NAME.TENANT_MANAGEMENT,
    includeRoute: ROUTE_NAME.TENANT_MANAGEMENT,
  },
  {
    icon: <Icons.Support />,
    activeIcon: <Icons.Support fill="white" />,
    label: "Support",
    routeName: ROUTE_NAME.SUPPORT,
  },
];

const SideDrawerNavigator = () => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { t } = useTranslation();
  const { user, isLoggedIn } = useAuthValue();

  const isCodingAdmin = user?.role === USER_ROLES.CODING_ADMIN;
  const isNurse = getUserRoleAccess(USER_ROLE_ACCESS_KEY.NURSE);
  const isSurgeon = user?.role === USER_ROLES.SURGEON;
  const forcedActiveRoute: string | undefined = state?.activeSideNav;
  const fromPath: string | undefined = state?.from?.path; // "/case-management"
  const forcedFromRoute = fromPath?.replace(/^\//, "");
  const sideDrawerOptions = useMemo(() => {
    let filteredOptions = optionsArray;

    if (isNurse) {
      filteredOptions = filteredOptions.filter((option) =>
        NURSE_ROUTES_ACCESS.includes(option.routeName),
      );
    }

    if (isCodingAdmin) {
      filteredOptions = filteredOptions.filter((option) =>
        CODING_ADMIN_ROUTES_ACCESS.includes(option.routeName),
      );
    }

    if (isSurgeon) {
      filteredOptions = filteredOptions.filter(
        (option) =>
          option.routeName === ROUTE_NAME.ROOT ||
          SURGEON_ROUTES_ACCESS.includes(option.routeName),
      );
    }

    if (pathname !== "/" + ROUTE_NAME.USER_ACCOUNT) {
      filteredOptions = filteredOptions.filter(
        (option) => option.routeName !== ROUTE_NAME.USER_ACCOUNT,
      );
    }

    return filteredOptions;
  }, [isCodingAdmin, isNurse, isSurgeon, pathname]);

  useEffect(() => {
    if (
      isLoggedIn &&
      isCodingAdmin &&
      !CODING_ADMIN_ROUTES_ACCESS.some((route) =>
        pathname.slice(1).startsWith(route),
      )
    ) {
      navigate("/" + ROUTE_NAME.CASE_MANAGEMENT);
    }
  }, [isCodingAdmin, isLoggedIn, navigate, pathname]);

  useEffect(() => {
    if (
      isLoggedIn &&
      isNurse &&
      !NURSE_ROUTES_ACCESS.some((route) =>
        pathname.slice(1).startsWith(route),
      )
    ) {
      navigate("/" + ROUTE_NAME.CASE_BOARD);
    }
  }, [isNurse, isLoggedIn, navigate, pathname]);

  useEffect(() => {
    if (
      isLoggedIn &&
      isSurgeon &&
      pathname !== "/" &&
      pathname !== "/" + ROUTE_NAME.LOGIN &&
      !pathname.slice(1).startsWith(ROUTE_NAME.DASHBOARD) &&
      !SURGEON_ROUTES_ACCESS.some((route) =>
        pathname.slice(1).startsWith(route),
      )
    ) {
      navigate("/" + ROUTE_NAME.CASE_MANAGEMENT);
    }
  }, [isSurgeon, isLoggedIn, navigate, pathname]);

  return (
    <Stack
      mt={8}
      pt={3}
      width={64}
      bgcolor="custom.background.navigation"
      sx={{ borderRight: "1px solid", borderRightColor: "custom.border.default" }}
    >
      {sideDrawerOptions.map((option) => {
        const { label, routeName, includeRoute } = option ?? {};
                const forced = forcedActiveRoute ?? forcedFromRoute;

       const isActive =
          (forced && routeName === forced) ||
          (!forced &&
            (pathname === `/${routeName}` ||
              (includeRoute && pathname?.includes(includeRoute))));

        const isSupport = routeName === ROUTE_NAME.SUPPORT;
        return (
          <div
            style={{
              display: "flex",
              flex: isSupport ? 1 : 0,
              flexDirection: "column",
            }}
            key={routeName}
          >
            {isSupport && <div style={globalStyles.flex1} />}
            <Button
              fullWidth
              key={label}
              onClick={() => navigate("/" + routeName, { replace: true })}
              sx={{
                py: 1.5,
                px: 0.5,
                bgcolor: isActive
                  ? "custom.foreground.primary"
                  : "custom.background.navigation",
                textTransform: "none",
                justifyContent: "center",
                flexDirection: "column",
                gap: 0.5,
                borderRadius: 0,
                color: isActive
                  ? "custom.background.primary"
                  : "custom.foreground.secondary",
                fontSize: 10,
                fontWeight: 700,
                "& .normalIcon": {
                  display: isActive ? "none" : "block",
                },
                "& .activeIcon": {
                  display: isActive ? "block" : "none",
                },
                ":hover": {
                  bgcolor: "custom.foreground.primary",
                  color: "custom.background.primary",
                  fontWeight: 700,
                  "& .normalIcon": {
                    display: "none",
                  },
                  "& .activeIcon": {
                    display: "block",
                  },
                },
              }}
            >
              <Box className="normalIcon">{option?.icon}</Box>
              <Box className="activeIcon">{option?.activeIcon}</Box>
              <Typography fontSize={9} fontWeight={700}>
                {t(label)}
              </Typography>
            </Button>
          </div>
        );
      })}
    </Stack>
  );
};

export default SideDrawerNavigator;
